import { isMobileDevice } from 'Utilities/devices';
import { Blocks, Photos, PremiumOffer, Seller } from 'Utilities/profiling/@types';

import { CONDITION_GRID } from '../ConfirmDetails/ConditionBlock';
import { DAMAGES_IN_WIZARD } from '../ConfirmDetails/ConfirmDetails.helpers';
import { getHistorySections, SERVICE_HISTORY_KEY, SERVICE_UPLOAD_KEY } from '../ServiceHistory/ServiceHistory.helpers';
import {
  HAS_NEVER_BEEN_INAPPROPRIATELY_USED, HAS_NEVER_BEEN_WRITTEN_OFF_OR_STOLEN,
  HAS_PROOF_OF_PURCHASE,
  IMAGE_COUNT_MINIMUM,
  PRIVATE_PLATE,
  PROFILING_PATH,
  SKIPPED_VALUE,
  VEHICLE_OWNER,
} from '../VehicleProfiling.constants';
import {
  getSectionList,
  isMustHaveProofOfPurchase,
  isNotEmptyLocation, isOwnerFilled, isPhotosResupply, isPrivatePlateFilled,
  isRetakeStatus, isServiceResupply,
  isTouched, PHOTOS_GRID as VEHICLE_PHOTOS,
  SKIPPED_PHOTOS,
} from '../VehicleProfiling.helpers';
import { useIsProfileConfirmed } from '../VehicleProfiling.hooks';
import LocalTexts from '../VehicleProfilingTexts.json';

import {
  BASIC_KEY, DETAILS_KEY, HISTORY_KEY, PART_OF_GLOBAL_PROGRESS, PHOTO_KEY,
} from './VehicleProfilingHub.constants';

const sectionsKeys = Object.keys(LocalTexts.vehicleProfiling.sections);

const V5C_LOGBOOK = 'v5cLogbook';
const VEHICLE_LOCATION = 'vehicleLocation';

type ProgressReturn<T = {}> = {
  current?: string;
  isConfirmed: boolean;
  partOfGlobalProgress: number;
  progress: number;
  start: string;
} & T;

type basicProgressProps = {
  state?: PremiumOffer;
}

const basicPathAndProgress = ({
  state,
} : basicProgressProps): ProgressReturn => {
  const { profileConfirmationDetails } = state || {};
  return {
    current: '/details',
    isConfirmed: !!profileConfirmationDetails?.contactDetailsConfirmedAt,
    partOfGlobalProgress: PART_OF_GLOBAL_PROGRESS.BASIC,
    progress: 100,
    start: '/details',
  };
};

type vehicleProgressProps = {
  isElectric: boolean;
  isHybrid: boolean;
  sellerData?: Seller;
  showBatteryOwnership: boolean;
  state?: PremiumOffer;
  text: Record<string, any>;
}

const vehiclePathAndProgress = ({
  isElectric, isHybrid,
  sellerData, showBatteryOwnership, state,
  text,
}: vehicleProgressProps): ProgressReturn => {
  const { vehicleDetails } = state || {};
  const mustHaveProofOfPurchase = isMustHaveProofOfPurchase(vehicleDetails?.keeperStartDate || '');

  const blocks: Blocks = getSectionList({
    isElectric, isHybrid, mustHaveProofOfPurchase, showBatteryOwnership, text,
  });
  const startPath = mustHaveProofOfPurchase ? PROFILING_PATH.PROOF_OF_PURCHASE : PROFILING_PATH.START;

  const mandatoryStart = {
    dpKey: {
      HAS_NEVER_BEEN_INAPPROPRIATELY_USED,
      HAS_NEVER_BEEN_WRITTEN_OFF_OR_STOLEN,
      ...(mustHaveProofOfPurchase && { HAS_PROOF_OF_PURCHASE }),
    },
    route: startPath,
  };

  const isMandatoryFilled = Object.values(mandatoryStart.dpKey)
    .every((it) => vehicleDetails?.[it as keyof typeof vehicleDetails]);
  let currentPath = isMandatoryFilled ? null : mandatoryStart.route;

  const filled = blocks.filter(([key, val]) => {
    let filledVal;
    const detailsVal = vehicleDetails?.[val.dpKey as keyof typeof vehicleDetails];

    switch (key) {
    case V5C_LOGBOOK:
      filledVal = isTouched(detailsVal) && detailsVal !== false;
      break;
    case VEHICLE_LOCATION:
      filledVal = isMandatoryFilled && isNotEmptyLocation(sellerData || {} as Seller);
      break;
    case VEHICLE_OWNER:
      filledVal = isOwnerFilled(vehicleDetails);
      break;
    case PRIVATE_PLATE:
      filledVal = isPrivatePlateFilled(state!);
      break;
    default:
      filledVal = isTouched(detailsVal);
      break;
    }

    if (val.dpKey && !filledVal && !currentPath) {
      currentPath = val.route;
    }

    return filledVal;
  });

  const percentage = Math.round(filled.length / (blocks.length / 100));
  const globalPercentage = Math.round(filled.length / (blocks.length / PART_OF_GLOBAL_PROGRESS.VEHICLE_DETAILS));

  return {
    current: currentPath ?? startPath,
    isConfirmed: !!state?.profileConfirmationDetails?.vehicleDetailsConfirmedAt,
    partOfGlobalProgress: globalPercentage,
    progress: percentage,
    start: percentage >= 100 ? PROFILING_PATH.SUMMARY : currentPath ?? startPath,
  };
};

type photosProgressProps = {
  photoUrl: string;
  photos: Photos;
  state?: PremiumOffer;
}

const photosPathAndProgress = (
  {
    photos, photoUrl, state,
  }: photosProgressProps,
): ProgressReturn => {
  const photosObj = photos.filter((x) => !isRetakeStatus(x.status))
    .reduce((acc, x) => ({ ...acc, ...{ [x.kind]: x } }), {});

  const { vehicleDetails } = state || {};
  const mandatoryPhotos = VEHICLE_PHOTOS.map(({ name }) => {
    const skipped = !photosObj[name as keyof typeof photosObj] || SKIPPED_PHOTOS.includes(name);

    if (skipped) {
      return null;
    }
    return photosObj[name as keyof typeof photosObj];
  }).filter((photo) => photo !== null);

  const damage = Object.values(DAMAGES_IN_WIZARD).filter((x) => isTouched(vehicleDetails?.[x]));

  const DAMAGE_COUNT_MINIMUM = CONDITION_GRID.length;

  const REQUIRED_NUMBER = IMAGE_COUNT_MINIMUM + DAMAGE_COUNT_MINIMUM;
  const CURRENT_NUMBER = mandatoryPhotos.length + damage.length;

  const percentage = Math.round((CURRENT_NUMBER / REQUIRED_NUMBER) * 100);
  const globalPercentage = Math.round((CURRENT_NUMBER / REQUIRED_NUMBER) * PART_OF_GLOBAL_PROGRESS.PHOTOS);

  const { profileConfirmationDetails } = state || {};
  const pathToPhotos = isMobileDevice({ includeTablet: false }) ? photoUrl : PROFILING_PATH.PHOTOS;

  return {
    current: pathToPhotos,
    isConfirmed:
      !!profileConfirmationDetails?.vehiclePhotosConfirmedAt
      && !!profileConfirmationDetails?.vehicleConditionConfirmedAt
      && !!profileConfirmationDetails?.serviceHistoryConfirmedAt,
    partOfGlobalProgress: Number(globalPercentage),
    progress: percentage,
    start: pathToPhotos,
  };
};

const ifUserHasServiceHistory = (dpValue: any, firstKeys: string[]) => {
  const keys = firstKeys.slice(0, 2);

  return keys.includes(dpValue);
};

type historyProgressProps = {
  hasResupplied?: boolean;
  serviceHistoryPhotos: Photos;
  state?: PremiumOffer;
  text: Record<string, any>;
}

const historyPathAndProgress = (
  {
    hasResupplied = true,
    serviceHistoryPhotos, state, text,
  }: historyProgressProps,
): ProgressReturn => {
  const photos = serviceHistoryPhotos?.some((photo) => photo?.isUploaded) ? serviceHistoryPhotos : [];
  const { profileConfirmationDetails, vehicleDetails } = state || {};
  const sections = getHistorySections(text);
  const firstSection = sections[0][1];
  const startPath = firstSection?.route as string;
  let currentPath: string | undefined;

  const filled = sections.filter(([key, val]) => {
    let filledVal;
    const questions = 'questions';
    const value = vehicleDetails?.[val.dpKey as keyof typeof vehicleDetails];
    const skipped = value === SKIPPED_VALUE;
    const firstKeys = Object.keys(firstSection[questions]);
    const firstVal = vehicleDetails?.[firstSection.dpKey as keyof typeof vehicleDetails];
    const hasService = ifUserHasServiceHistory(firstVal, firstKeys);

    if (key === SERVICE_HISTORY_KEY) {
      filledVal = !hasResupplied && !hasService ? false : isTouched(value);
    }

    if (key === SERVICE_UPLOAD_KEY) {
      filledVal = hasService
        ? (photos?.length > 0 || skipped) && hasResupplied
        : isTouched(firstVal);
    }

    currentPath = (!filledVal && !currentPath) ? val.route : currentPath;

    return filledVal;
  });

  const percentage = Math.round(filled.length / (sections.length / 100));
  const globalPercentage = Math.round(filled.length / (sections.length / PART_OF_GLOBAL_PROGRESS.SERVICE_HISTORY));

  return {
    current: currentPath,
    isConfirmed: !!profileConfirmationDetails?.serviceHistoryConfirmedAt,
    partOfGlobalProgress: globalPercentage,
    progress: percentage,
    start: startPath,
  };
};

type SectionsReturn = ProgressReturn<{
  isBlocked?: boolean;
  isLocked?: boolean;
  key: string;
  resupply?: boolean;
}>;

type useGetSectionsParams = {
  mapFunction: (key: string) => any;
  photosBlocked: boolean;
  profilingStatus: PremiumOffer['profilingStatus'];
  resupply?: boolean;
  serviceHistoryResupplied?: boolean;
}

const useGetSections = (
  { mapFunction, photosBlocked, profilingStatus, resupply, serviceHistoryResupplied }: useGetSectionsParams,
): Record<string, SectionsReturn> => {
  const isProfileConfirmed = useIsProfileConfirmed();

  const sections = sectionsKeys.map(mapFunction).reduce<Record<string, SectionsReturn>>((acc, section) => {
    acc[section.key] = section;
    return acc;
  }, {});

  sections[PHOTO_KEY].isBlocked = photosBlocked;
  sections[HISTORY_KEY].isBlocked = photosBlocked;

  if (resupply) {
    sections[BASIC_KEY].isLocked = resupply;
    sections[DETAILS_KEY].isLocked = resupply;

    if (isServiceResupply(profilingStatus)
      && !isPhotosResupply(profilingStatus)
      && sections[PHOTO_KEY].progress < 100
      && isProfileConfirmed) {
      sections[PHOTO_KEY].progress = 100;
      sections[PHOTO_KEY].isLocked = true;
    }

    if (sections[PHOTO_KEY].progress < 100
      || isPhotosResupply(profilingStatus)) {
      sections[PHOTO_KEY].resupply = true;
      sections[PHOTO_KEY].progress = 80;
    } else {
      sections[PHOTO_KEY].isLocked = true;
    }

    if (isServiceResupply(profilingStatus)) {
      sections[HISTORY_KEY].resupply = !serviceHistoryResupplied;
      sections[HISTORY_KEY].progress = !serviceHistoryResupplied ? 80 : sections[HISTORY_KEY].progress;
    } else {
      sections[HISTORY_KEY].isLocked = true;
    }
  }

  return sections;
};

export {
  basicPathAndProgress,
  vehiclePathAndProgress,
  photosPathAndProgress,
  historyPathAndProgress,
  useGetSections,
  type SectionsReturn,
};
